import type { ChecklistItem } from '../../../../types/schema'
import type { StockItem } from '../../../../types/stock'
import type { XRayAnchor, XRayEdge } from '../xrayTypes'

export interface ChecklistResolverInput {
  itemId?: string
  workspaceId?: string
  allItems: ChecklistItem[]
  stockItems?: StockItem[]
  sliceId?: string
}

export const resolveChecklistAnchor = (input: ChecklistResolverInput): XRayAnchor => {
  const { itemId, allItems, stockItems = [], sliceId } = input
  const page = 'checklist'

  // canvas-blockers: Show all blockers across all items
  if (sliceId === 'canvas-blockers' || !itemId) {
    const edges: XRayEdge[] = []
    const itemMap = new Map<string, ChecklistItem>(allItems.map((item) => [item.id, item]))

    for (const item of allItems) {
      if (item.blockedByNodeIds && item.blockedByNodeIds.length > 0) {
        for (const link of item.blockedByNodeIds) {
          if (itemMap.has(link.targetId)) {
            edges.push({
              fromId: link.targetId,
              toId: item.id,
              kind: 'blocker',
              label: 'blocks',
            })
          }
        }
      }
      if (item.isBlockerForNodeIds && item.isBlockerForNodeIds.length > 0) {
        for (const link of item.isBlockerForNodeIds) {
          if (itemMap.has(link.targetId)) {
            const exists = edges.some(
              (e) => e.fromId === item.id && e.toId === link.targetId && e.kind === 'blocker',
            )
            if (!exists) {
              edges.push({
                fromId: item.id,
                toId: link.targetId,
                kind: 'blocker',
                label: 'blocks',
              })
            }
          }
        }
      }
    }

    return {
      id: 'canvas',
      page,
      screenPosition: null,
      edges,
      destinations: [],
    }
  }

  const target = allItems.find((item) => item.id === itemId)
  if (!target) {
    return {
      id: itemId,
      page,
      screenPosition: null,
      edges: [],
      destinations: [],
    }
  }

  const edges: XRayEdge[] = []

  // gate-inputs: if sliceId is gate-inputs or target is a gate
  if (sliceId === 'gate-inputs' || target.nodeKind === 'GATE_AND' || target.nodeKind === 'GATE_OR') {
    if (target.gateInputIds && target.gateInputIds.length > 0) {
      for (const inputId of target.gateInputIds) {
        edges.push({
          fromId: inputId,
          toId: target.id,
          kind: 'gate-input',
          label: 'gate input',
        })
      }
    }
  }

  // direct blockers:
  // 1) items that target is blocked by (blocker -> target)
  if (target.blockedByNodeIds && target.blockedByNodeIds.length > 0) {
    for (const link of target.blockedByNodeIds) {
      edges.push({
        fromId: link.targetId,
        toId: target.id,
        kind: 'blocker',
        label: 'blocks',
      })
    }
  }
  // 2) items that target blocks (target -> blocked item)
  if (target.isBlockerForNodeIds && target.isBlockerForNodeIds.length > 0) {
    for (const link of target.isBlockerForNodeIds) {
      const exists = edges.some(
        (e) => e.fromId === target.id && e.toId === link.targetId && e.kind === 'blocker',
      )
      if (!exists) {
        edges.push({
          fromId: target.id,
          toId: link.targetId,
          kind: 'blocker',
          label: 'blocks',
        })
      }
    }
  }

  // Stock coupling: stockItem where stockItem.linkedTaskIds.includes(target.id) OR target.requiredStockIds includes stockItem.id
  for (const stockItem of stockItems) {
    const isLinked =
      stockItem.linkedTaskIds?.includes(target.id) ||
      target.requiredStockIds?.includes(stockItem.id) ||
      target.stockRequirements?.some((req) => req.stockItemId === stockItem.id)

    if (isLinked) {
      const req = target.stockRequirements?.find((r) => r.stockItemId === stockItem.id)
      const qtyLabel = req ? `needs ${req.quantity} ${stockItem.unit || 'units'}` : undefined
      edges.push({
        fromId: stockItem.id,
        toId: target.id,
        kind: 'stock',
        label: qtyLabel,
      })
    }
  }

  return {
    id: target.id,
    page,
    screenPosition: null, // will be set by caller from DOM if available
    edges,
    destinations: [],
  }
}
